import { Badge, ActionIcon, Box, Card, Group, Modal, SimpleGrid, Stack, Text, Title, UnstyledButton, Button } from '@mantine/core';
import { IconCalendar, IconCheck, IconChevronLeft, IconChevronRight, IconTrash } from '@tabler/icons-react';
import { format, isValid, parse, parseISO } from 'date-fns';
import { useMemo, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getProfessionalAvailability, bulkDeleteAvailability } from '../../services/AvailabilityService';
import { useApp } from '../../context/AppContext';
import { primaryColor } from '../../constants/theme';
import { AvailableTime } from '../../types/AvailabilityModels';
import { DatePickerModal } from './DatePickerModal';
import { DeleteAvailabilityModal } from './DeleteAvailabilityModal';
import { DeleteOption } from './deleteAvailabilityOptions';

interface ManageAvailabilityTabProps {
  refreshKey?: number;
}

interface AvailabilityDateButton {
  isoDate: string;
  slotCount: number;
}

const parseAvailabilityDate = (dateStr: string): Date => parseISO(dateStr);

const buildAvailabilityDateButtons = (
  availableDates: { availableTimes: { startIso: string }[] }[]
): AvailabilityDateButton[] => {
  const slotsByDate: Record<string, number> = {};

  for (const d of availableDates) {
    for (const time of d.availableTimes) {
      const parsed = parseISO(time.startIso);
      if (!isValid(parsed)) continue;
      const key = format(parsed, 'yyyy-MM-dd');
      slotsByDate[key] = (slotsByDate[key] ?? 0) + 1;
    }
  }

  return Object.entries(slotsByDate)
    .map(([isoDate, slotCount]) => ({ isoDate, slotCount }))
    .sort((a, b) => a.isoDate.localeCompare(b.isoDate));
};

interface DateButtonProps {
  isoDate: string;
  slotCount: number;
  isSelected: boolean;
  onSelect: (isoDate: string) => void;
}

const DateButton = ({ isoDate, slotCount, isSelected, onSelect }: DateButtonProps) => {
  const date = parseAvailabilityDate(isoDate);
  if (!isValid(date)) return null;
  const dayOfWeek = format(date, 'EEE');
  const dayOfMonth = format(date, 'd');
  const month = format(date, 'MMM');

  return (
    <UnstyledButton
      onClick={() => onSelect(isoDate)}
      data-testid={`date-button-${isoDate}`}
      style={{
        border: `1px solid ${primaryColor}`,
        borderRadius: 10,
        padding: '10px 8px',
        width: 74,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        backgroundColor: isSelected ? primaryColor : 'transparent',
        flexShrink: 0,
        transition: 'background-color 0.15s ease',
      }}
    >
      <Text
        size="xs"
        fw={600}
        c={isSelected ? 'white' : 'dimmed'}
        style={{ lineHeight: 1.2 }}
      >
        {dayOfWeek}
      </Text>
      <Text
        size="xl"
        fw={700}
        c={isSelected ? 'white' : 'dark'}
        style={{ lineHeight: 1.1 }}
      >
        {dayOfMonth}
      </Text>
      <Text
        size="xs"
        fw={500}
        c={isSelected ? 'white' : 'dimmed'}
        style={{ lineHeight: 1.2 }}
      >
        {month}
      </Text>
      <Badge
        radius="xl"
        size="xs"
        variant={isSelected ? 'white' : 'light'}
        color={isSelected ? 'white' : primaryColor}
        mt={4}
        style={{
          color: isSelected ? primaryColor : 'white',
          backgroundColor: isSelected ? 'white' : primaryColor,
          whiteSpace: 'nowrap',
          fontSize: 8,
        }}
      >
        {slotCount} {slotCount === 1 ? 'slot' : 'slots'}
      </Badge>
    </UnstyledButton>
  );
};

export const ManageAvailabilityTab = ({ refreshKey }: ManageAvailabilityTabProps) => {
  const { token, profile } = useApp();
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [pendingDeleteSlot, setPendingDeleteSlot] = useState<AvailableTime | null>(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  const scroll = (direction: 'left' | 'right') => {
    scrollRef.current?.scrollBy({ left: direction === 'left' ? -200 : 200, behavior: 'smooth' });
  };

  const queryKey = ['professionalAvailability', profile?.id, refreshKey];

  const { data: availability } = useQuery({
    queryKey,
    queryFn: () => getProfessionalAvailability(profile!.id, token!),
    enabled: !!token && !!profile?.id,
  });

  const dateButtons = useMemo(
    () => buildAvailabilityDateButtons(availability?.availableDates ?? []),
    [availability?.availableDates]
  );

  const enabledDates = useMemo(
    () => new Set(dateButtons.map((b) => b.isoDate)),
    [dateButtons]
  );

  const handleCalendarConfirm = (dateStr: string) => {
    // dateStr is in 'dd MMM yyyy' format from DatePickerModal
    const parsed = parse(dateStr, 'dd MMM yyyy', new Date());
    if (isValid(parsed)) {
      setSelectedDate(format(parsed, 'yyyy-MM-dd'));
    }
    setCalendarOpen(false);
  };

  useEffect(() => {
    if (dateButtons.length > 0 && selectedDate === null) {
      setSelectedDate(dateButtons[0].isoDate);
    }
  }, [dateButtons, selectedDate]);

  useEffect(() => {
    if (!selectedDate || !scrollRef.current) return;
    const btn = scrollRef.current.querySelector<HTMLElement>(
      `[data-testid="date-button-${selectedDate}"]`
    );
    if (btn) {
      btn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [dateButtons, selectedDate]);

  const allSlots = useMemo(
    () => availability?.availableDates.flatMap((d) => d.availableTimes) ?? [],
    [availability?.availableDates]
  );

  const selectedSlots = useMemo((): AvailableTime[] => {
    if (!selectedDate || !availability) return [];
    return availability.availableDates
      .flatMap((d) => d.availableTimes)
      .filter((t) => {
        const parsed = parseISO(t.startIso);
        return isValid(parsed) && format(parsed, 'yyyy-MM-dd') === selectedDate;
      })
      .sort((a, b) => parseISO(a.startIso).getTime() - parseISO(b.startIso).getTime());
  }, [availability, selectedDate]);

  const handleDeleteConfirm = async (_option: DeleteOption, affectedIds: number[]): Promise<void> => {
    if (!token) throw new Error('Not authenticated');
    await bulkDeleteAvailability(affectedIds, token);
    setPendingDeleteSlot(null);
    setSuccessMessage(`${affectedIds.length} slot${affectedIds.length === 1 ? '' : 's'} deleted.`);
    await queryClient.invalidateQueries({ queryKey });
  };

  return (
    <Stack gap="sm">
      <Group justify="space-between" align="center">
        <Title order={4} fw="normal">
          Date
        </Title>
        <UnstyledButton
          onClick={() => setCalendarOpen(true)}
          data-testid="manage-availability-open-calendar"
          style={{ display: 'flex', alignItems: 'center', gap: 4 }}
        >
          <IconCalendar size={16} color={primaryColor} />
          <Text size="sm" c="dimmed">
            Month
          </Text>
        </UnstyledButton>
      </Group>

      <DatePickerModal
        opened={calendarOpen}
        label="Select date"
        initialDate={selectedDate ?? ''}
        onClose={() => setCalendarOpen(false)}
        onConfirm={handleCalendarConfirm}
        enabledDates={enabledDates}
      />

      {dateButtons.length === 0 ? (
        <Text c="dimmed" size="sm" data-testid="manage-availability-empty">
          No available dates found.
        </Text>
      ) : (
        <Group gap={4} align="center" wrap="nowrap" data-testid="manage-availability-date-row">
          <ActionIcon
            variant="subtle"
            color={primaryColor}
            onClick={() => scroll('left')}
            aria-label="Scroll dates left"
            data-testid="manage-availability-scroll-left"
          >
            <IconChevronLeft size={18} />
          </ActionIcon>
          <Box
            ref={scrollRef}
            data-testid="manage-availability-scroll-container"
            style={{
              display: 'flex',
              gap: 8,
              overflowX: 'auto',
              paddingBottom: 4,
              flex: 1,
              scrollbarWidth: 'none',
            }}
          >
            {dateButtons.map((btn) => (
              <DateButton
                key={btn.isoDate}
                isoDate={btn.isoDate}
                slotCount={btn.slotCount}
                isSelected={selectedDate === btn.isoDate}
                onSelect={setSelectedDate}
              />
            ))}
          </Box>
          <ActionIcon
            variant="subtle"
            color={primaryColor}
            onClick={() => scroll('right')}
            aria-label="Scroll dates right"
            data-testid="manage-availability-scroll-right"
          >
            <IconChevronRight size={18} />
          </ActionIcon>
        </Group>
      )}

      {selectedDate && (
        <Stack gap="xs" mt="xs" data-testid="manage-availability-slot-list">
          <Title order={4} fw="normal">
            {format(parseISO(selectedDate), 'EEEE, d MMMM yyyy')}
          </Title>
          {selectedSlots.length === 0 ? (
            <Stack align="center" gap="xs" py="xl" data-testid="manage-availability-no-slots">
              <Box
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  backgroundColor: 'var(--mantine-color-gray-1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <IconCalendar size={28} color="var(--mantine-color-gray-5)" />
              </Box>
              <Text fw={500} c="dimmed" size="sm">No slots for this date</Text>
              <Text c="dimmed" size="xs">Add availability to get started</Text>
            </Stack>
          ) : (
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="sm" data-testid="manage-availability-slots-grid">
              {selectedSlots.map((slot) => {
              const topicColour = slot.topics?.[0]?.colour ?? primaryColor;
              const topicName = slot.topics?.length
                ? slot.topics.map((t) => t.title).join(', ')
                : 'Open Session';
              const startLabel = format(parseISO(slot.startIso), 'HH:mm');
              const endLabel = format(parseISO(slot.endIso), 'HH:mm');
              const costLabel = slot.rate === 0 ? 'Free' : `£${slot.rate}`;

              return (
                <Card
                  key={slot.id}
                  withBorder
                  radius="md"
                  p="sm"
                  shadow="none"
                  data-testid={`slot-card-${slot.id}`}
                  style={{ borderColor: 'var(--mantine-color-gray-3)', boxShadow: 'none' }}
                >
                  <Group wrap="nowrap" gap="sm" align="center">
                    <Box
                      style={{
                        width: 6,
                        minWidth: 6,
                        alignSelf: 'stretch',
                        borderRadius: 3,
                        backgroundColor: topicColour,
                      }}
                      data-testid={`slot-colour-${slot.id}`}
                    />
                    <Stack gap={2} style={{ flex: 1 }}>
                      <Text fw={600} size="sm" ta="left" data-testid={`slot-topic-${slot.id}`}>
                        {topicName}
                      </Text>
                      <Text size="xs" c="dimmed" ta="left" data-testid={`slot-time-${slot.id}`}>
                        {startLabel} – {endLabel}
                      </Text>
                      <Text size="xs" fw={600} c={primaryColor} ta="left" data-testid={`slot-cost-${slot.id}`}>
                        {costLabel}
                      </Text>
                    </Stack>
                    {slot.id && (() => {
                      const isPast = parseISO(slot.startIso) < new Date();
                      if (slot.available === false) {
                        return (
                          <Badge
                            radius="sm"
                            color="orange"
                            variant="light"
                            data-testid={`slot-booked-${slot.id}`}
                          >
                            Booked
                          </Badge>
                        );
                      }
                      if (isPast) {
                        return (
                          <Badge
                            radius="sm"
                            color="gray"
                            variant="light"
                            data-testid={`slot-past-${slot.id}`}
                          >
                            Past
                          </Badge>
                        );
                      }
                      return (
                        <ActionIcon
                          color="red"
                          variant="subtle"
                          onClick={() => setPendingDeleteSlot(slot)}
                          aria-label="Delete slot"
                          data-testid={`slot-delete-${slot.id}`}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      );
                    })()}
                  </Group>
                </Card>
              );
            })}
            </SimpleGrid>
          )}
        </Stack>
      )}

      <DeleteAvailabilityModal
        opened={pendingDeleteSlot !== null}
        slot={pendingDeleteSlot}
        allSlots={allSlots}
        bookedIds={new Set(allSlots.filter((s) => s.available === false && s.id != null).map((s) => s.id as number))}
        onClose={() => setPendingDeleteSlot(null)}
        onConfirm={handleDeleteConfirm}
      />

      <Modal
        opened={!!successMessage}
        onClose={() => setSuccessMessage(null)}
        withCloseButton={false}
        centered
        size="sm"
      >
        <Stack gap="md" align="center" py="sm" data-testid="delete-success-modal">
          <Box
            style={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              backgroundColor: '#22c55e',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <IconCheck size={40} color="white" stroke={2.5} />
          </Box>
          <Title order={4} fw={600} ta="center">
            Success!
          </Title>
          <Text ta="center" c="dimmed" size="sm">
            {successMessage}
          </Text>
          <Button color={primaryColor} onClick={() => setSuccessMessage(null)} fullWidth data-testid="delete-success-ok">
            OK
          </Button>
        </Stack>
      </Modal>
    </Stack>
  );
};
